import { useEffect, useSyncExternalStore } from 'react';
import { type LiveState, streamStore } from './stream';

/**
 * 화면이 스토어를 보는 유일한 통로 (결정 2: 상태 관리 라이브러리 없음).
 * `getState`는 프레임마다 바뀔 때만 새 객체를 돌려주므로 `useSyncExternalStore`가 그대로 쓴다.
 *
 * 상태 전체를 받으면 반영마다(스냅샷 한 장만 와도) 다시 그린다. 실시간 화면처럼 거의 모든 칸을
 * 보는 곳만 이것을 쓰고, 나머지는 `useLiveSelector`로 필요한 칸만 본다.
 */
export function useLiveState(): LiveState {
  return useSyncExternalStore(streamStore.subscribe, streamStore.getState, streamStore.getState);
}

/**
 * 상태의 한 조각만 본다. 고른 값이 앞과 같으면(`Object.is`) 반영이 와도 다시 그리지 않는다.
 *
 * 스토어는 바뀐 칸만 새 배열로 갈아 끼우므로 `(state) => state.alerts`처럼 **칸을 그대로 돌려주는**
 * 셀렉터를 쓴다. 셀렉터 안에서 새 배열·객체를 만들면 매번 다르다고 보여 전체 구독과 같아진다.
 * 숫자·문자열은 새로 계산해도 된다(`(state) => state.instances.length`).
 */
export function useLiveSelector<T>(select: (state: LiveState) => T): T {
  const read = () => select(streamStore.getState());
  return useSyncExternalStore(streamStore.subscribe, read, read);
}

/** 화면이 살아 있는 동안만 SSE를 붙여 둔다 */
export function useStreamConnection(): void {
  useEffect(() => {
    streamStore.start();
    return () => streamStore.stop();
  }, []);
}
